"use client";

// @i18n-only nl, de — it only ever shows on the Dutch and German sites.

import { useEffect, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { BASKET_EVENT, hasOffSiteCards } from "@/lib/basket";
import { isUsLocale } from "@/lib/market";

const DISMISSED_KEY = "vellum_cards_offsite_dismissed";

/**
 * Christmas cards sell on the US site only, and the basket leaves a card line out on /nl and /de
 * (lib/basket). This line says why the cards are missing and where they still are, once per visit,
 * so nobody pays for the poster thinking the cards came with it. Renders nothing on /en.
 */
export function CardsOffSiteNotice() {
  const locale = useLocale();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isUsLocale(locale)) return;
    const check = () => {
      let dismissed = false;
      try {
        dismissed = window.sessionStorage.getItem(DISMISSED_KEY) === "1";
      } catch {
        // sessionStorage unavailable: show it.
      }
      setShow(!dismissed && hasOffSiteCards());
    };
    check();
    window.addEventListener(BASKET_EVENT, check);
    return () => window.removeEventListener(BASKET_EVENT, check);
  }, [locale]);

  if (isUsLocale(locale) || !show) return null;
  return <Notice onClose={() => {
    try {
      window.sessionStorage.setItem(DISMISSED_KEY, "1");
    } catch {
      // ignore
    }
    setShow(false);
  }} />;
}

function Notice({ onClose }: { onClose: () => void }) {
  const t = useTranslations("cart");
  return (
    <div className="border-b border-rule bg-paper-deep" role="status">
      <div className="mx-auto flex max-w-page items-start gap-3 px-5 py-3 md:px-6 lg:px-12">
        <p className="flex-1 font-sans text-sm leading-[1.5] text-ink">
          {t("cardsOffSite")}{" "}
          <a className="underline underline-offset-4" href="/en/christmas-cards">
            {t("cardsOffSiteLink")}
          </a>
        </p>
        <button
          aria-label={t("close")}
          className="-my-2 inline-flex min-h-[44px] min-w-[44px] items-center justify-center font-sans text-lg text-ink-soft hover:text-ink"
          onClick={onClose}
          type="button"
        >
          ×
        </button>
      </div>
    </div>
  );
}
